import { useState } from 'react';
import { SEO } from '../components/SEO';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';

const faqs = [
  {
    question: 'How do I claim a casino bonus?',
    answer: 'To claim a casino bonus, click on the offer link, register a new account with the casino, and make a qualifying deposit. Some bonuses are automatically credited, while others may require entering a promo code during registration or deposit.'
  },
  {
    question: 'Are the casinos on SpinEmerge safe?',
    answer: 'Yes, all casinos featured on SpinEmerge are licensed by the UK Gambling Commission (UKGC) and use advanced SSL encryption to protect your personal and financial information. We only recommend casinos that meet strict security and fairness standards.'
  },
  {
    question: 'What are wagering requirements?',
    answer: 'Wagering requirements (also called playthrough requirements) specify how many times you must bet the bonus amount before you can withdraw winnings. For example, a £100 bonus with 35x wagering means you must wager £3,500 before withdrawing.'
  },
  {
    question: 'How quickly can I withdraw my winnings?',
    answer: 'Withdrawal times vary by casino and payment method. E-wallets like PayPal, Skrill, and Neteller typically process within 24 hours. Bank transfers usually take 3-5 business days. Some casinos offer instant withdrawals for VIP members.'
  },
  {
    question: 'Can I play on mobile devices?',
    answer: 'Yes, all recommended casinos offer mobile-optimized websites, and many have dedicated mobile apps. You can play slots, table games, and live casino games on smartphones and tablets running iOS or Android.'
  },
  {
    question: 'What payment methods are accepted?',
    answer: 'Most UK casinos accept major credit/debit cards (Visa, Mastercard), e-wallets (PayPal, Skrill, Neteller), bank transfers, and some accept cryptocurrencies. Payment options vary by casino, so check individual reviews for details.'
  },
  {
    question: 'Are there any fees for deposits or withdrawals?',
    answer: 'Most UK-licensed casinos don\'t charge fees for deposits or withdrawals. However, some payment providers may charge fees. Always check the casino\'s banking page for the most current fee information.'
  },
  {
    question: 'What is responsible gambling?',
    answer: 'Responsible gambling means playing within your means, setting limits, and recognizing when to stop. We promote responsible gambling and provide links to organizations like GambleAware and GamCare that offer support and resources for problem gambling.'
  },
  {
    question: 'Can I play for free without depositing?',
    answer: 'Many casinos offer demo or free play modes for slots and table games, allowing you to try games without risking real money. However, you cannot win real money in demo mode, and live casino games typically require real money deposits.'
  },
  {
    question: 'How do I know if a casino is fair?',
    answer: 'UK-licensed casinos are regularly audited by independent testing agencies like eCOGRA and iTech Labs. These audits verify that games use Random Number Generators (RNGs) and that the casino pays out winnings fairly. Look for certification badges on casino websites.'
  }
];

const jsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map(faq => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer
    }
  }))
};

const FAQItem = ({ question, answer, isOpen, onToggle }: { question: string; answer: string; isOpen: boolean; onToggle: () => void }) => {
  return (
    <div className="bg-dark-800 border border-gray-700 rounded-xl overflow-hidden hover:border-primary-500 transition-colors">
      <button
        onClick={onToggle}
        className="w-full px-6 py-4 text-left flex items-center justify-between focus:outline-none"
      >
        <span className="text-white font-semibold pr-4">{question}</span>
        {isOpen ? (
          <FaChevronUp className="text-primary-400 flex-shrink-0" />
        ) : (
          <FaChevronDown className="text-primary-400 flex-shrink-0" />
        )}
      </button>
      {isOpen && (
        <div className="px-6 pb-4">
          <p className="text-gray-300 leading-relaxed">{answer}</p>
        </div>
      )}
    </div>
  );
};

export const FAQ = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <>
      <SEO
        title="Frequently Asked Questions - Casino FAQ"
        description="Find answers to common questions about casino bonuses, wagering requirements, withdrawals, payment methods, and more."
        canonical="/faq"
        jsonLd={jsonLd}
      />

      <div className="min-h-screen pt-20 pb-20">
        <section className="py-12 bg-gradient-to-br from-dark-900 via-dark-800 to-dark-900">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-3xl mx-auto mb-12">
              <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary-400 to-accent-400 bg-clip-text text-transparent">
                Frequently Asked Questions
              </h1>
              <p className="text-xl text-gray-300">
                Everything you need to know about casino bonuses, withdrawals, and more
              </p>
            </div>

            <div className="max-w-4xl mx-auto space-y-4">
              {faqs.map((faq, index) => (
                <FAQItem
                  key={index}
                  question={faq.question}
                  answer={faq.answer}
                  isOpen={openIndex === index}
                  onToggle={() => toggleFAQ(index)}
                />
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

